import { z } from 'zod';

import { shouldIncludeItem } from '@/lib/formHelpers';

export const contractVehicleFormSchema = z.object({
	contractVehicleId: z.string().optional(),
	vehicleId: z.string().optional(), // References existing vehicle
	plateNumber: z.string().optional(), // For display only - not sent in mutation
	isVip: z.boolean().default(false),
	rfidTag: z.string().optional(),
	isActive: z.boolean().default(true),
	isDeleted: z.boolean().optional().default(false) // Support soft-delete for existing vehicles
});

export const contractFormSchema = z
	.object({
		customerId: z
			.string({ required_error: 'Select a customer.' })
			.min(1, 'Select a customer.'),
		beginDate: z
			.string({ required_error: 'Begin date is required.' })
			.min(1, 'Begin date is required.')
			.refine(value => !Number.isNaN(new Date(value).getTime()), {
				message: 'Begin date must be a valid date.'
			}),
		endDate: z
			.string({ required_error: 'End date is required.' })
			.min(1, 'End date is required.')
			.refine(value => !Number.isNaN(new Date(value).getTime()), {
				message: 'End date must be a valid date.'
			}),
		amount: z
			.string({ required_error: 'Monthly amount is required.' })
			.trim()
			.refine(value => {
				const numeric = Number(value);
				return !Number.isNaN(numeric) && numeric > 0;
			}, 'Monthly amount must be a positive number.'),
		isAutoRenewed: z.boolean(),
		simultaneousVehicleLimit: z
			.string()
			.optional()
			.transform(value => (value ?? '').trim()),
		reservedParkingSlots: z
			.string()
			.optional()
			.transform(value => (value ?? '').trim())
			.refine(value => {
				if (!value) return true;
				const numeric = Number(value);
				return !Number.isNaN(numeric) && numeric >= 0;
			}, 'Reserved parking slots must be 0 or greater.'),
		isActive: z.boolean(),
		preferredLanguage: z.enum(['EN', 'EL', '']).default(''),
		vehicles: z.array(contractVehicleFormSchema).optional().default([]),
		notes: z.array(z.string()).optional()
	})
	.superRefine((values, ctx) => {
		const begin = new Date(values.beginDate);
		const end = new Date(values.endDate);

		if (begin.getTime() > end.getTime()) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ['endDate'],
				message: 'End date must be on or after the begin date.'
			});
		}
	});

export type ContractVehicleFormValues = z.infer<
	typeof contractVehicleFormSchema
>;
export type ContractFormValues = z.infer<typeof contractFormSchema>;

export interface ContractVehicleSubmitData {
	contractVehicleId?: string;
	vehicleId?: string; // References existing vehicle
	rfidTag?: string | null;
	isVip: boolean;
	isDeleted?: boolean; // Support soft-delete for existing vehicles
}

export interface ContractFormSubmitData {
	customerId: string;
	beginDate: string;
	endDate: string;
	amount: number;
	isAutoRenewed: boolean;
	simultaneousVehicleLimit?: number | null;
	reservedParkingSlots?: number | null;
	isActive: boolean;
	preferredLanguage: 'EN' | 'EL' | '' | null;
	vehicles: ContractVehicleSubmitData[];
}

export const defaultContractFormValues: ContractFormValues = {
	customerId: '',
	beginDate: '',
	endDate: '',
	amount: '',
	isAutoRenewed: true,
	simultaneousVehicleLimit: '',
	reservedParkingSlots: '',
	isActive: true,
	preferredLanguage: '',
	vehicles: []
};

export const buildContractFormSubmitData = (
	values: ContractFormValues
): ContractFormSubmitData => {
	const parseNumeric = (input: string | undefined | null) => {
		if (!input) {
			return null;
		}

		const numeric = Number(input);
		return Number.isNaN(numeric) ? null : numeric;
	};

	return {
		customerId: values.customerId,
		beginDate: values.beginDate,
		endDate: values.endDate,
		amount: Number(values.amount),
		isAutoRenewed: values.isAutoRenewed,
		simultaneousVehicleLimit: parseNumeric(values.simultaneousVehicleLimit),
		reservedParkingSlots: parseNumeric(values.reservedParkingSlots),
		isActive: values.isActive,
		preferredLanguage: values.preferredLanguage || null,
		vehicles: (values.vehicles ?? [])
			.filter(vehicle => !vehicle.isDeleted) // Exclude soft-deleted vehicles
			.filter(vehicle =>
				// Include vehicles that have a valid vehicleId
				shouldIncludeItem(vehicle, 'vehicleId', 'contractVehicleId')
			)
			.map(vehicle => ({
				contractVehicleId: vehicle.contractVehicleId,
				vehicleId: vehicle.vehicleId,
				rfidTag: (vehicle.rfidTag ?? '').trim() || null,
				isVip: vehicle.isVip ?? false,
				isDeleted: vehicle.isDeleted ?? false
			}))
	};
};
